import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export const submitProductReview = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({
      product_slug: z.string().min(1).max(200),
      rating: z.number().int().min(1).max(5),
      title: z.string().max(300).optional().default(""),
      body: z.string().min(10).max(4000),
      reviewer_name: z.string().min(2).max(200),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const { userId } = context;
    const { data: userRow, error: userErr } = await supabaseAdmin.auth.admin.getUserById(userId);
    if (userErr || !userRow.user?.email) {
      throw new Error("Could not resolve user email");
    }
    const { data: result, error } = await supabaseAdmin.schema("private" as never).rpc(
      "submit_product_review" as never,
      {
        _uid: userId,
        _email: userRow.user.email,
        _product_slug: data.product_slug,
        _rating: data.rating,
        _title: data.title,
        _body: data.body,
        _reviewer_name: data.reviewer_name,
      } as never,
    );
    if (error) {
      const knownMessages = [
        "Product not found",
        "Rating must be between 1 and 5",
        "Reviewer name must be 2 to 200 characters",
        "Title must be 300 characters or fewer",
        "Review must be at least 10 characters",
        "Review too long (max 4000 chars)",
        "Too many review submissions. Please try again later.",
      ];
      const msg = error.message ?? "";
      console.error("submit_product_review failed:", error);
      throw new Error(
        knownMessages.some((k) => msg.includes(k))
          ? msg
          : "Failed to submit review. Please try again.",
      );
    }
    return result as { status: "published" | "pending_approval"; id: string };
  });

export type ProductRating = { count: number; avg: number };
export type ProductRatingMap = Record<string, ProductRating>;

/**
 * Public read of real review aggregates per product slug.
 * Returns ONLY products that actually have ≥1 review — callers should treat
 * a missing slug as "no reviews yet" and render no rating row.
 *
 * No auth required: product_reviews has a public SELECT RLS policy.
 */
export const getProductRatings = createServerFn({ method: "GET" })
  .inputValidator((d) =>
    z.object({
      slugs: z.array(z.string().min(1).max(200)).max(200).optional(),
    }).parse(d ?? {}),
  )
  .handler(async ({ data }) => {
    let query = supabaseAdmin
      .from("product_reviews")
      .select("product_slug,rating");
    if (data.slugs && data.slugs.length > 0) {
      query = query.in("product_slug", data.slugs);
    }
    const { data: rows, error } = await query;
    if (error) {
      console.error("getProductRatings failed:", error);
      return {} as ProductRatingMap;
    }
    const acc: Record<string, { sum: number; count: number }> = {};
    for (const r of rows ?? []) {
      const slug = (r as { product_slug: string }).product_slug;
      const rating = (r as { rating: number }).rating;
      if (!acc[slug]) acc[slug] = { sum: 0, count: 0 };
      acc[slug].sum += rating;
      acc[slug].count += 1;
    }
    const out: ProductRatingMap = {};
    for (const [slug, { sum, count }] of Object.entries(acc)) {
      if (count > 0) out[slug] = { count, avg: sum / count };
    }
    return out;
  });
